import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Divider, Autocomplete, CircularProgress, Chip, LinearProgress, Stack, InputAdornment, useMediaQuery, useTheme, Paper, Tooltip, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import CategoriaService from '../../services/categorias';
import CustomerService from '../../services/customers';
// import ImagenesService from '../../services/imagenes'; // reemplazado por llamada directa para progreso
import ImagenesService from '../../services/imagenes';
import ImagenesAIService from '../../services/imagenesAI';
import ArticuloService from '../../services/articulos';
import UploadImage from '../../components/UploadImage';
import { OPERATION } from '../../helpers/enums';
import { ccyFormat } from '../../helpers/helper';

// Esquema de validación para nueva publicación "Para pedir o comprar"
const schema = Yup.object().shape({
  name: Yup.string().required('Título requerido'),
  description: Yup.string().required('Descripción requerida'),
  category_id: Yup.string().required('Categoría requerida'),
  customer_id: Yup.string().required('Proveedor requerido'),
  list_price: Yup.number().typeError('Costo inválido').positive('Debe ser > 0').required('Costo requerido'),
  sale_price: Yup.number().typeError('Precio inválido').positive('Debe ser > 0').required('Precio requerido')
    .test('greater-than-cost', 'Debe ser mayor que el costo', function (value) {
      const { list_price } = this.parent;
      if (!list_price || !value) return true;
      return Number(value) > Number(list_price);
    }),
});

const initialValues = {
  name: '',
  description: '',
  category_id: '',
  customer_id: '',
  list_price: '',
  sale_price: '',
  images: [],
  tags: [],
  hasWholesale: false,
  wholesaleDiscount: 200 // descuento fijo ejemplo (ajustable luego o podría venir de config)
};

const NewPublication = ({ store, onSuccess, onError, navigateBack }) => {
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingCust, setLoadingCust] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [imagePrompt, setImagePrompt] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        setError(null);
        if (!values.category_id) {
          setError('Seleccioná una categoría');
          setSaving(false);
          return;
        }
        // subir imágenes si hay
        let uploaded = [];
        if (images.length > 0) {
          const fd = new FormData();
            images.forEach(img => fd.append('image', img));
          if (imagePrompt.trim()) {
            fd.append('prompt', imagePrompt.trim());
            uploaded = await ImagenesAIService.generate(fd);
          } else {
            uploaded = await ImagenesService.upload(fd);
          }
        }
        const payload = {
          article: {
            name: values.name,
            description: values.description,
            images: uploaded,
            sku: '',
            tags: values.tags || [],
          },
          category: { _id: values.category_id },
          customer: { _id: values.customer_id },
          prices: {
            list_price: Number(values.list_price),
            sale_price: Number(values.sale_price),
          },
          images: uploaded,
          name: values.name,
          description: values.description,
          category_id: values.category_id,
          customer_id: values.customer_id,
          quantity: 0,
          news: true,
          to: OPERATION.VENDER,
          store_id: store?._id || null,
        };
        const saved = store?._id ? await ArticuloService.saveArticleInStore(store._id, payload) : await ArticuloService.save(payload);
        onSuccess && onSuccess(saved, {
          whatsappDraft: {
            name: values.name,
            description: values.description,
            salePrice: values.sale_price,
            hasWholesale: values.hasWholesale,
            wholesaleDiscount: values.wholesaleDiscount,
            sku: saved?.sku || saved?.article?.sku || payload?.sku || payload?.article?.sku || '',
            images: Array.isArray(saved?.article?.images) && saved.article.images.length
              ? saved.article.images
              : uploaded
          }
        });
        navigateBack && navigateBack();
      } catch (e) {
        const msg = e.message || 'Error al guardar';
        setError(msg);
        onError && onError(msg);
      } finally {
        setSaving(false);
        setUploadProgress(0);
      }
    }
  });

  useEffect(() => {
    const loadCats = async () => {
      try {
        setLoadingCats(true);
        const resp = await CategoriaService.getAll();
        setCategories(resp);
      } finally {
        setLoadingCats(false);
      }
    };
    const loadCust = async () => {
      try {
        setLoadingCust(true);
        const resp = await CustomerService.getAll();
        setCustomers(resp);
      } finally {
        setLoadingCust(false);
      }
    };
    if (!categories.length) loadCats();
    if (!customers.length) loadCust();
  }, [categories.length, customers.length]);

  const { values, errors, handleChange, setFieldValue, handleSubmit, isValid } = formik;

  const formatNumberView = (val) => {
    if (val === '' || val === null || typeof val === 'undefined') return '';
    if (isNaN(Number(val))) return val;
    return Number(val).toLocaleString('es-AR');
  };

  // Atajo Ctrl+Enter para enviar si válido
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (isValid && !saving && !(uploadProgress > 0 && uploadProgress < 100)) {
          handleSubmit();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isValid, saving, uploadProgress, handleSubmit]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const marginValue = (values.list_price && values.sale_price)
    ? values.sale_price - values.list_price
    : null;
  const marginPct = marginValue !== null
    ? (marginValue / values.list_price * 100)
    : null;
  const marginColor = marginPct !== null
    ? marginPct >= 30 ? 'success' : marginPct >= 10 ? 'primary' : 'warning'
    : 'default';

  return (
    <Box component={Paper} elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: { xs: 2, sm: 3 }, gap: 2, maxWidth: 840, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1}>
        <Typography variant='h6' sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>Nueva Publicación</Typography>
        {navigateBack && <Button size='small' variant='outlined' onClick={navigateBack}>Volver</Button>}
      </Stack>
      <Divider />
  <Box
    id='new-publication-form'
    component='form'
    onSubmit={handleSubmit}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      pr: { xs: 0, sm: 0.5 },
      WebkitOverflowScrolling: 'touch'
    }}
  >
        <Stack spacing={2}>
          <TextField
            id='name'
            label='Título'
            placeholder='Nombre del artículo'
            size={isMobile ? 'small' : 'medium'}
            fullWidth
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <TextField
            id='description'
            label='Descripción'
            placeholder='Describe brevemente el artículo'
            multiline
            minRows={3}
            size={isMobile ? 'small' : 'medium'}
            fullWidth
            value={values.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='stretch'>
          <Autocomplete
            options={categories}
            getOptionLabel={o => o.name}
            loading={loadingCats}
            value={categories.find(c => c._id === values.category_id) || null}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            disableClearable
            onChange={(e, val) => setFieldValue('category_id', val?._id || '')}
            renderInput={(params) => <TextField {...params} label='Categoría' size={isMobile ? 'small' : 'medium'} error={Boolean(errors.category_id)} helperText={errors.category_id} />}
            sx={{ flex: 1 }}
          />
          <Autocomplete
            options={customers}
            getOptionLabel={o => o.name}
            loading={loadingCust}
            value={customers.find(c => c._id === values.customer_id) || null}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(e, val) => setFieldValue('customer_id', val?._id || '')}
            renderInput={(params) => <TextField {...params} label='Proveedor' size={isMobile ? 'small' : 'medium'} error={Boolean(errors.customer_id)} helperText={errors.customer_id} />}
            sx={{ flex: 1 }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='flex-start'>
          <TextField
            id='list_price'
            label='Costo'
            type='text'
            inputMode='decimal'
            size={isMobile ? 'small' : 'medium'}
            value={formatNumberView(values.list_price)}
            onChange={(e) => {
              const raw = e.target.value.replace(/\./g, '').replace(/,/g, '').replace(/[^0-9]/g, '');
              const num = raw === '' ? '' : Number(raw);
              setFieldValue('list_price', num);
            }}
            error={Boolean(errors.list_price)}
            helperText={errors.list_price}
            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <TextField
            id='sale_price'
            label='Precio venta estimado'
            type='text'
            inputMode='decimal'
            size={isMobile ? 'small' : 'medium'}
            value={formatNumberView(values.sale_price)}
            onChange={(e) => {
              const raw = e.target.value.replace(/\./g, '').replace(/,/g, '').replace(/[^0-9]/g, '');
              const num = raw === '' ? '' : Number(raw);
              setFieldValue('sale_price', num);
            }}
            error={Boolean(errors.sale_price)}
            helperText={errors.sale_price}
            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
            sx={{ flex: 1 }}
          />
          {marginValue !== null && (
            <Chip
              size={isMobile ? 'small' : 'medium'}
              color={marginColor}
              label={`Margen: $ ${ccyFormat(marginValue)} (${marginPct.toFixed(1)}%)`}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
          <FormControlLabel
            control={<Checkbox checked={values.hasWholesale} onChange={e => setFieldValue('hasWholesale', e.target.checked)} />}
            label='Tiene precio mayorista'
          />
          {values.hasWholesale && (
            <TextField
              label='Descuento mayorista (resta)'
              size={isMobile ? 'small' : 'medium'}
              type='number'
              value={values.wholesaleDiscount}
              onChange={e => setFieldValue('wholesaleDiscount', Number(e.target.value) || 0)}
              sx={{ width: 220 }}
              InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
            />
          )}
        </Stack>

        <Autocomplete
          multiple
            freeSolo
          options={[]}
          value={values.tags}
          onChange={(e, val) => setFieldValue('tags', val)}
          renderInput={(params) => <TextField {...params} size={isMobile ? 'small' : 'medium'} label='Tags (Enter para agregar)' placeholder='Ej: verano, oferta, importado' />}
        />

        <Box>
          <UploadImage onImagesSelected={(imgs) => setImages(imgs)} uploadImage showImages={false} />
          <TextField
            size='small'
            fullWidth
            placeholder='Prompt IA para imágenes (opcional)'
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            sx={{ mt: 0.75 }}
          />
          {images.length > 0 && (
            <Box mt={1} sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(86px,1fr))', gap: 1 }}>
              {images.map((file, idx) => {
                const objectUrl = URL.createObjectURL(file);
                return (
                  <Box key={idx} position='relative' sx={{ width: '100%', pt: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Box component='img' src={objectUrl} alt={`imagen seleccionada ${file.name}`} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Tooltip title='Eliminar'>
                      <IconButton
                        aria-label={`Eliminar ${file.name}`}
                        size='small'
                        onClick={() => {
                          setImages(prev => prev.filter((_, i) => i !== idx));
                          URL.revokeObjectURL(objectUrl);
                        }}
                        sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                      >
                        <DeleteIcon fontSize='inherit' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <Box display='flex' flexDirection='column' gap={1}>
            <LinearProgress variant='determinate' value={uploadProgress} />
            <Typography variant='caption'>{`Subiendo imágenes: ${uploadProgress}%`}</Typography>
          </Box>
        )}
        {error && <Typography color='error' variant='caption'>{error}</Typography>}

        {/* Desktop / tablet action bar */}
        <Stack direction='row' justifyContent='flex-end' gap={2} mt={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <Button variant='outlined' onClick={navigateBack} disabled={saving}>Cancelar</Button>
          <Button type='submit' variant='contained' disabled={!isValid || saving || (uploadProgress > 0 && uploadProgress < 100)} startIcon={saving && <CircularProgress size={16} />}>
            Guardar publicación
          </Button>
        </Stack>
      </Box>

      {/* Mobile sticky action bar placeholder (will refine in later step) */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, position: 'sticky', bottom: 0, left: 0, right: 0, bgcolor: 'background.paper', py: 1, borderTop: '1px solid', borderColor: 'divider', gap: 1, mt: 1, zIndex: 3 }}>
        <Button fullWidth variant='outlined' onClick={navigateBack} disabled={saving}>Cancelar</Button>
        <Button fullWidth type='submit' form='new-publication-form' variant='contained' disabled={!isValid || saving || (uploadProgress > 0 && uploadProgress < 100)} startIcon={saving && <CircularProgress size={16} />}>
          Guardar
        </Button>
      </Box>
    </Box>
  );
};

export default NewPublication;
